import React, { useState } from 'react'
import type { Asset, UpNextConfig } from '@/organisms/AngelPlayer/BitmovinPlayer/types'
import type { HlsPlayerController } from '@/organisms/AngelPlayer/HlsPlayer/HlsPlayerController'
import { HlsBufferingOverlay } from './HlsBufferingOverlay'
import { HlsCenterPlayOverlay } from './HlsCenterPlayOverlay'
import { HlsControlBar } from './HlsControlBar'
import { HlsPlayerUIProvider } from './HlsPlayerUIContext'
import { HlsSkipIntroButton } from './HlsSkipIntroButton'
import { HlsSubtitleOverlay } from './HlsSubtitleOverlay'
import { HlsTitleBar } from './HlsTitleBar'
import { HlsUpNextOverlay } from './HlsUpNextOverlay'
import { useControlBarVisibility } from './useControlBarVisibility'
import { useVideoState } from './useVideoState'

interface HlsPlayerUIProps {
  videoRef: React.RefObject<HTMLVideoElement | null>
  controller: HlsPlayerController | null
  containerRef: React.RefObject<HTMLElement | null>
  asset: Asset
  introStartTime?: number | null
  introEndTime?: number | null
  episodeGuid?: string
  upNext?: UpNextConfig
  showContentShareButton?: boolean
  onShare?: () => void
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  onOpenParentalControls?: () => void
}

/**
 * Top-level UI overlay for the HLS.js player.
 * Sets up the HlsPlayerUIContext (video state + controller) and renders:
 * - Title bar overlay
 * - Subtitle overlay
 * - Buffering spinner
 * - Skip intro button (when intro timestamps are available)
 * - Up next overlay (when up-next config is available)
 * - Center play/pause overlay (click-to-play on video area)
 * - Bottom control bar with all playback controls
 */
export const HlsPlayerUI: React.FC<HlsPlayerUIProps> = ({
  videoRef,
  controller,
  containerRef,
  asset,
  introStartTime,
  introEndTime,
  episodeGuid,
  upNext,
  showContentShareButton,
  onShare,
  showParentalControlsButton,
  parentalControlsEnabled,
  parentalControlsApplied,
  parentalControlsTotal,
  onOpenParentalControls,
}) => {
  const videoState = useVideoState(videoRef, containerRef)
  const { visible, onControlBarInteraction } = useControlBarVisibility(containerRef, videoState.playing)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const hasIntro = introStartTime != null && introEndTime != null

  const contextValue = {
    currentTime: videoState.currentTime,
    duration: videoState.duration,
    buffered: videoState.buffered,
    playing: videoState.playing,
    volume: videoState.volume,
    muted: videoState.muted,
    fullscreen: videoState.fullscreen,
    waiting: videoState.waiting,
    playbackSpeed: videoState.playbackSpeed,
    controller,
    title: asset.sourceConfig.subtitle,
    videoRef,
    settingsOpen,
  }

  return (
    <HlsPlayerUIProvider value={contextValue}>
      <HlsTitleBar visible={visible} />
      <HlsSubtitleOverlay />
      <HlsBufferingOverlay />
      {hasIntro && (
        <HlsSkipIntroButton introStartTime={introStartTime} introEndTime={introEndTime} episodeGuid={episodeGuid} />
      )}
      {upNext && <HlsUpNextOverlay upNext={upNext} controlsVisible={visible} />}
      <HlsCenterPlayOverlay visible={visible} />
      <HlsControlBar
        visible={visible}
        onInteraction={onControlBarInteraction}
        showContentShareButton={showContentShareButton}
        onShare={onShare}
        showParentalControlsButton={showParentalControlsButton}
        parentalControlsEnabled={parentalControlsEnabled}
        parentalControlsApplied={parentalControlsApplied}
        parentalControlsTotal={parentalControlsTotal}
        onOpenParentalControls={onOpenParentalControls}
        subtitleTrackCount={controller?.getSubtitleList().length ?? 0}
        audioTrackCount={controller?.getAvailableAudio().length ?? 0}
        onSettingsOpenChange={setSettingsOpen}
      />
    </HlsPlayerUIProvider>
  )
}
